import { useContext } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  AuthAndPersonalDetailsFormFields,
  CustomerTypeAndSegment,
} from "@/components/Onboarding/Steps/PersonalDetails/personalDetails.types.ts";
import { DataContext } from "@/provider/DataContextProvider.tsx";
import { checkCscLogin, useUpdateCustomer } from "@/services/api.ts";
import { BasicCustomerData } from "@/services/model";
import {
  transformBasicCustomerDataToPersonalDetailsForm,
  transformPersonalDetailsFormToBasicCustomerData,
} from "@/utils/customerUtils.ts";
import { createReferenceId } from "@/utils/initializeSentry";
import { log } from "@/utils/log";

export const useHandleUpdateCustomer = (
  persistCustomerAndGoToCheckout: (
    customerData: BasicCustomerData,
    target?: string,
  ) => void,
  formMethods: UseFormReturn<AuthAndPersonalDetailsFormFields>,
  formValues: AuthAndPersonalDetailsFormFields,
  openVerifyEmailModal: () => void,
): {
  handleCustomerUpdate: handleUpdateCustomerFunction;
  isUpdatingCustomer: boolean;
} => {
  const { mutateAsync: updateCustomer, isLoading: isUpdatingCustomer } =
    useUpdateCustomer();
  const { setDataContext } = useContext(DataContext);
  const { t, i18n } = useTranslation();
  const baseLanguage = i18n.language.split("-")[0];

  const handleCustomerUpdate = (
    customerData: BasicCustomerData,
    customerTypeAndCustomerSegment: CustomerTypeAndSegment,
  ) => {
    const referenceId = createReferenceId();
    const customerDataToSubmit = {
      ...transformPersonalDetailsFormToBasicCustomerData(
        formValues,
        customerData,
        baseLanguage,
        t,
      ),
      ...customerTypeAndCustomerSegment,
    };

    updateCustomer({
      data: customerDataToSubmit,
    })
      .then((data) => {
        setDataContext((prev) => ({
          ...prev,
          personalDetails:
            transformBasicCustomerDataToPersonalDetailsForm(data),
        }));

        if (data.email) {
          checkCscLogin(data.email, {
            headers: {
              "X-Reference-Id": referenceId,
            },
          }).then(async (response) => {
            setDataContext((prev) => ({
              ...prev,
              isEmailVerified: response.emailVerified,
            }));

            if (response.emailVerified) {
              persistCustomerAndGoToCheckout(customerDataToSubmit);
            } else if (response.emailVerified === false) {
              openVerifyEmailModal();
            }
          });
        }
      })
      .catch((error) => {
        if (`${error?.response?.data}`.includes("invalid phone number")) {
          formMethods.setError("phone", {
            type: "backendError",
          });
        } else {
          log("Error in customer form: " + error);
        }
      });
  };

  return {
    handleCustomerUpdate,
    isUpdatingCustomer,
  };
};

export type handleUpdateCustomerFunction = (
  customerData: BasicCustomerData,
  customerTypeAndCustomerSegment: CustomerTypeAndSegment,
) => void;
